@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import ai.wagemix.net.client.WMOccupationStatsCollectionRequestBuilder
import ai.wagemix.net.client.WagemixClientAPI
import ai.wagemix.net.models.toKxs
import com.fsryan.foundation.data.FSSyncDataSource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The stats and reference-data fetches of the Wagemix API (sync data source playbook). Each
 * entry point is non-suspending, generates its own sequence id, is guarded per operation and
 * scope, emits `Initial` then exactly one terminal event, and writes what it fetched to the
 * local context (stats) or the cache (taxonomy, areas, industries). It never vends data.
 */
interface WMStatsSyncDataSource: FSSyncDataSource<WMStatsSyncEvent<*>> {

    /**
     * Fetches the stats collection for the request [modify] shapes. The in-flight guard and the
     * local-context key are [statsSignature] of the same request, so a presenter reads the result
     * back with `stats(statsSignature(modify))`.
     */
    fun syncStats(additionalContext: Map<String, Any> = emptyMap(), modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit)
    fun syncTaxonomy(additionalContext: Map<String, Any> = emptyMap())
    fun syncAreas(occupationIds: Set<Int>, additionalContext: Map<String, Any> = emptyMap())
    fun syncIndustries(occupationIds: Set<Int>, additionalContext: Map<String, Any> = emptyMap())
}

/**
 * The identity of a stats request: its occupations with weights, area and industry, in a stable
 * order. Two requests with the same signature fetch the same collection.
 *
 * @throws IllegalStateException when [modify] leaves the request without occupations
 */
fun statsSignature(modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit): String {
    val builder = WMOccupationStatsCollectionRequestBuilder().apply(modify)
    val weighted = builder.requestedOccupations.map { (occupation, weight) -> "$occupation:$weight" }.sorted()
    if (weighted.isEmpty()) {
        throw IllegalStateException("At least one occupation ID must be provided")
    }
    return "o=${weighted.joinToString(",")}|a=${builder.requestedAreaId ?: 1}|i=${builder.requestedIndustryId ?: 1}"
}

fun WMStatsSyncDataSource(api: WagemixClientAPI, cache: WMCache, localContext: WMLocalContextDataSource): WMStatsSyncDataSource {
    return WMStatsSyncDataSourceImpl(api = api, cache = cache, localContext = localContext)
}

internal class WMStatsSyncDataSourceImpl(
    private val api: WagemixClientAPI,
    private val cache: WMCache,
    localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): WMBaseSyncDataSource<WMStatsSyncEvent<*>>(localContext, scope), WMStatsSyncDataSource {

    override fun syncStats(additionalContext: Map<String, Any>, modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit) {
        val sequenceId = Uuid.random()
        launch {
            val signature = try {
                statsSignature(modify)
            } catch (e: IllegalStateException) {
                // A request that never left the device: a terminal Failure with no Initial (playbook, Step 6).
                WMStatsSyncEvent.Stats.Failure("", e, sequenceId, additionalContext).emit()
                return@launch
            }
            guarded(
                operation = OP_STATS,
                scope = signature,
                sequenceId = sequenceId,
                initial = { WMStatsSyncEvent.Stats.Initial(signature, sequenceId, additionalContext) },
                success = { WMStatsSyncEvent.Stats.Success(signature, sequenceId, additionalContext) },
                failure = { error -> WMStatsSyncEvent.Stats.Failure(signature, error, sequenceId, additionalContext) },
                remote = { api.fetchOccupationStatsCollection(modify).dataOrThrow { "No stats in the response" } },
                local = { stats -> localContext.upsertStats(signature, stats) }
            )
        }
    }

    override fun syncTaxonomy(additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_TAXONOMY,
                scope = "",
                sequenceId = sequenceId,
                initial = { WMStatsSyncEvent.Taxonomy.Initial(sequenceId, additionalContext) },
                success = { WMStatsSyncEvent.Taxonomy.Success(sequenceId, additionalContext) },
                failure = { error -> WMStatsSyncEvent.Taxonomy.Failure(error, sequenceId, additionalContext) },
                remote = { api.fetchOccupationsTaxonomy().dataOrThrow { "No taxonomy in the response" } },
                local = { taxonomy -> cache.upsertTaxonomy(WMCache.Value.Taxonomy(taxonomy.toKxs())) }
            )
        }
    }

    override fun syncAreas(occupationIds: Set<Int>, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_AREAS,
                scope = occupationIds.sorted().joinToString(","),
                sequenceId = sequenceId,
                initial = { WMStatsSyncEvent.Areas.Initial(occupationIds, sequenceId, additionalContext) },
                success = { WMStatsSyncEvent.Areas.Success(occupationIds, sequenceId, additionalContext) },
                failure = { error -> WMStatsSyncEvent.Areas.Failure(occupationIds, error, sequenceId, additionalContext) },
                remote = {
                    api.fetchAreasWithData { occupationIds.forEach { addOccupationId(it) } }.dataOrThrow { "No areas in the response" }
                },
                local = { areas -> cache.upsertAreas(WMCache.Value.Areas(areas.map { it.toKxs() })) }
            )
        }
    }

    override fun syncIndustries(occupationIds: Set<Int>, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_INDUSTRIES,
                scope = occupationIds.sorted().joinToString(","),
                sequenceId = sequenceId,
                initial = { WMStatsSyncEvent.Industries.Initial(occupationIds, sequenceId, additionalContext) },
                success = { WMStatsSyncEvent.Industries.Success(occupationIds, sequenceId, additionalContext) },
                failure = { error -> WMStatsSyncEvent.Industries.Failure(occupationIds, error, sequenceId, additionalContext) },
                remote = {
                    api.fetchIndustriesWithData { occupationIds.forEach { addOccupationId(it) } }
                        .dataOrThrow { "No industries in the response" }
                },
                local = { industries -> cache.upsertIndustries(WMCache.Value.Industries(industries.map { it.toKxs() })) }
            )
        }
    }

    private companion object {
        const val OP_STATS = "stats.fetch"
        const val OP_TAXONOMY = "stats.taxonomy"
        const val OP_AREAS = "stats.areas"
        const val OP_INDUSTRIES = "stats.industries"
    }
}
